import React, { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import type { FileRecord, TableMeta } from '../services/api';
import './Visualiser.css';

interface Props {
  file: FileRecord;
  tables: TableMeta[];
  selectedTableId: string | null;
  onSelectTable: (id: string | null) => void;
}

export default function Visualiser({ file, tables, selectedTableId, onSelectTable }: Props) {
  const api = useApi();
  const [depGraph, setDepGraph] = useState<Record<string, string[]>>({});
  const [sample, setSample] = useState<{ headers: string[]; rows: Record<string, unknown>[] } | null>(null);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [activeSheet, setActiveSheet] = useState<string | null>(file.sheets?.[0]?.sheet_id || null);

  useEffect(() => {
    setDepGraph(file.dependency_graph || {});
  }, [file]);

  useEffect(() => {
    if (!selectedTableId) { setSample(null); return; }
    setSampleLoading(true);
    api.get(`/excel/tables/${selectedTableId}/sample?rows=8`)
      .then(r => setSample({ headers: r.data.headers, rows: r.data.sample }))
      .catch(() => setSample(null))
      .finally(() => setSampleLoading(false));
  }, [selectedTableId, api]);

  const sheetTables = (sheetId: string) => tables.filter(t => t.sheet_id === sheetId);
  const selectedTable = tables.find(t => t.table_id === selectedTableId);

  const hasDeps = Object.values(depGraph).some(v => v.length > 0);

  return (
    <div className="visualiser">
      {/* Sheet tabs */}
      <div className="vis-sheet-tabs">
        {file.sheets?.map(s => (
          <button
            key={s.sheet_id}
            className={`vis-sheet-tab ${activeSheet === s.sheet_id ? 'active' : ''}`}
            onClick={() => setActiveSheet(s.sheet_id)}
          >
            {s.sheet_name}
            <span className="vis-sheet-count">{s.table_ids.length}</span>
          </button>
        ))}
      </div>

      <div className="vis-body">
        {/* Table map */}
        <div className="vis-map">
          {activeSheet && sheetTables(activeSheet).length === 0 && (
            <div className="vis-empty">
              <span style={{ fontSize: 32 }}>📭</span>
              No tables detected in this sheet
            </div>
          )}
          {activeSheet && sheetTables(activeSheet).map(t => {
            const deps = depGraph[t.table_id] || [];
            const isSelected = t.table_id === selectedTableId;
            const hasDep = deps.length > 0;
            const isDependedOn = Object.entries(depGraph).some(([, v]) => v.includes(t.table_id));
            return (
              <div
                key={t.table_id}
                className={`vis-table-card ${isSelected ? 'selected' : ''} ${hasDep ? 'has-deps' : ''}`}
                onClick={() => onSelectTable(isSelected ? null : t.table_id)}
              >
                <div className="vis-table-header">
                  <span className="vis-table-location">
                    {t.start_col}{t.start_row}:{t.end_col}{t.end_row}
                  </span>
                  <div className="vis-table-badges">
                    {hasDep && <span className="badge badge-yellow" title="Has formula dependencies">🔗</span>}
                    {isDependedOn && <span className="badge badge-accent" title="Referenced by other tables">⬅</span>}
                  </div>
                </div>
                <div className="vis-table-cols">
                  {t.headers.slice(0, 8).map(h => (
                    <span key={h} className="vis-col-chip">{h}</span>
                  ))}
                  {t.headers.length > 8 && <span className="vis-col-more">+{t.headers.length - 8}</span>}
                </div>
                <div className="vis-table-meta">
                  <span>{t.row_count} rows</span>
                  <span>{t.col_count} cols</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Detail panel */}
        <div className="vis-detail">
          {!selectedTable ? (
            <div className="vis-detail-empty">
              <span style={{ fontSize: 36, opacity: 0.5 }}>📊</span>
              <p>Select a table from the left to inspect its columns, types, and sample data</p>
            </div>
          ) : (
            <div className="vis-detail-content fade-in">
              <div className="vis-detail-header">
                <h3>Table at {selectedTable.start_col}{selectedTable.start_row}:{selectedTable.end_col}{selectedTable.end_row}</h3>
                <span className="badge badge-accent">{selectedTable.row_count} rows × {selectedTable.col_count} cols</span>
              </div>

              {/* Column types */}
              <div className="vis-detail-section">
                <p className="vis-detail-label">Columns</p>
                <div className="vis-col-list">
                  {selectedTable.columns?.map(c => (
                    <div key={c.column_name} className="vis-col-row">
                      <span className="vis-col-name">{c.column_name}</span>
                      <span className={`badge ${c.data_type === 'numeric' ? 'badge-green' : c.data_type === 'datetime' ? 'badge-yellow' : 'badge-accent'}`}>
                        {c.data_type}
                      </span>
                      <span className="vis-col-samples">{c.sample_values?.slice(0, 2).join(', ')}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dependencies */}
              {(depGraph[selectedTable.table_id] || []).length > 0 && (
                <div className="vis-detail-section">
                  <p className="vis-detail-label">Depends on</p>
                  {depGraph[selectedTable.table_id].map(tid => {
                    const dep = tables.find(t => t.table_id === tid);
                    return (
                      <span key={tid} className="vis-dep-chip" onClick={() => onSelectTable(tid)}>
                        {dep ? `${dep.start_col}${dep.start_row}` : tid.slice(0, 8)}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Sample data */}
              <div className="vis-detail-section">
                <p className="vis-detail-label">Sample data</p>
                {sampleLoading ? (
                  <span className="spinner" />
                ) : sample ? (
                  <div className="vis-sample-table-wrap">
                    <table className="vis-sample-table">
                      <thead>
                        <tr>{sample.headers.map(h => <th key={h}>{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {sample.rows.map((row, i) => (
                          <tr key={i}>
                            {sample.headers.map(h => <td key={h}>{String(row[h] ?? '')}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dependency graph summary */}
      {hasDeps && (
        <div className="vis-dep-graph card">
          <p className="vis-detail-label">Formula Dependency Graph</p>
          <div className="vis-dep-edges">
            {Object.entries(depGraph).filter(([, v]) => v.length > 0).map(([src, targets]) => {
              const srcT = tables.find(t => t.table_id === src);
              return targets.map(tgt => {
                const tgtT = tables.find(t => t.table_id === tgt);
                return (
                  <div key={`${src}-${tgt}`} className="vis-dep-edge">
                    <span className="vis-dep-node" onClick={() => onSelectTable(src)}>
                      {srcT ? `${srcT.start_col}${srcT.start_row}` : src.slice(0, 8)}
                    </span>
                    <span className="vis-dep-arrow">→</span>
                    <span className="vis-dep-node" onClick={() => onSelectTable(tgt)}>
                      {tgtT ? `${tgtT.start_col}${tgtT.start_row}` : tgt.slice(0, 8)}
                    </span>
                  </div>
                );
              });
            })}
          </div>
        </div>
      )}
    </div>
  );
}
